// The menus (docs/design/menu-bar.md): every command says where it goes, and one pure model of
// the tree feeds the browser's bar, the native menu bar and the shortcuts sheet. Which menus
// exist and which sections each holds is a table; a placement names a section of its own menu or
// the build fails. The shell's table is MENUS; the aligner's page (src/align) passes its own
// through the host, and every function here reads the table from there.

import { displayChord } from './keymap.ts'
import type { Platform } from './chords.ts'
import type { Registry } from './registry.ts'
import type { HomeEntry } from '../engine/protocol.ts'

export const MENUS = {
  app: { label: 'Interplae', sections: ['about', 'settings'] },
  file: { label: 'File', sections: ['open', 'project', 'export', 'import', 'closeout', 'server', 'home'] },
  edit: { label: 'Edit', sections: ['history', 'clipboard', 'find', 'format', 'settings'] },
  view: { label: 'View', sections: ['views', 'filter', 'panes', 'layout', 'home'] },
  segment: { label: 'Segment', sections: ['go', 'review', 'structure', 'flags', 'block'] },
  translation: { label: 'Translation', sections: ['matches', 'suggestions', 'sources', 'machine', 'document'] },
  window: { label: 'Window', sections: [] },
  help: { label: 'Help', sections: ['find', 'about'] },
} as const

/// A menu bar's table: each menu's label and its sections in order, by id. Every table has the
/// shell's `app`, `edit`, `window` and `help` menus, since FOLD and the hosts read them.
export type MenuTable = Readonly<Record<string, { readonly label: string; readonly sections: readonly string[] }>>
export type ShellMenus = typeof MENUS

export type MenuId<T extends MenuTable = ShellMenus> = keyof T & string
type SectionOf<M extends MenuId<T>, T extends MenuTable = ShellMenus> = T[M]['sections'][number]

/// The menus in the order a menu bar shows them.
export const MENU_ORDER: readonly MenuId[] = ['app', 'file', 'edit', 'view', 'segment', 'translation', 'window', 'help']

/// The menus a page draws, and the ids of the table's menus in their order.
export interface MenuBarTable<T extends MenuTable = ShellMenus> {
  menus: T
  order: readonly string[]
}

export const SHELL_MENUS: MenuBarTable = { menus: MENUS, order: MENU_ORDER }

export type SystemItem = 'undo' | 'redo' | 'cut' | 'copy' | 'paste' | 'selectAll'

export type Unplaced = 'a key' | 'a picker key' | 'acts on a DOM focus the menu takes' | 'the bar itself' | 'a match card\'s control' | 'the status line\'s offer' | 'the Home\'s sidebar'

export type Placement<S, T extends MenuTable = ShellMenus> =
  | { kind: 'unplaced'; reason: Unplaced }
  | {
      [M in MenuId<T>]: {
        kind: 'item'
        menu: M
        section: SectionOf<M, T>
        /// Shorter than the title. The palette and tooltips keep the title.
        label?: string
        /// Items of one section with the same submenu fold into one, placed at the first.
        submenu?: string
        /// Asks before acting, so the label ends in an ellipsis; 'native' when only the host's
        /// save panel asks, so the browser shows none.
        asks?: true | 'native'
        /// The native menu binds the chord as a key equivalent (native-app.md decision 9).
        accelerator?: true
        /// Only where the system draws the menu bar; the browser leaves the item out.
        native?: true
        /// In the menus only while this holds; otherwise absent, not disabled. The aligner's Send to
        /// memory… is a Manager's or an Admin's alone (brief 49).
        present?: (shell: S) => boolean
        checked?: (shell: S) => boolean
        /// The native Edit menu takes the system's item; the browser runs the focused-text adapter.
        system?: SystemItem
      }
    }[MenuId<T>]

export type Placed<S, T extends MenuTable = ShellMenus> = Extract<Placement<S, T>, { kind: 'item' }>

/// Shorthands for `commands.ts`, so a placement reads as one line.
export const unplaced = (reason: Unplaced): { kind: 'unplaced'; reason: Unplaced } => ({ kind: 'unplaced', reason })
export function at<S, M extends MenuId>(menu: M, section: SectionOf<M>, rest: Omit<Extract<Placed<S>, { menu: M }>, 'kind' | 'menu' | 'section'> = {} as never): Placement<S> {
  return { kind: 'item', menu, section, ...rest } as Placement<S>
}

/// `at` over another table, for a page with menus of its own.
export function placer<T extends MenuTable>(_table: T) {
  return <S, M extends MenuId<T>>(menu: M, section: SectionOf<M, T>, rest: Omit<Extract<Placed<S, T>, { menu: M }>, 'kind' | 'menu' | 'section'> = {} as never): Placement<S, T> =>
    ({ kind: 'item', menu, section, ...rest }) as Placement<S, T>
}

/// Where application-menu items go on a host without one (the browser, Windows).
export const FOLD = { about: { menu: 'help', section: 'about' }, settings: { menu: 'edit', section: 'settings' } } as const

/// Who draws the menus: the page's own bar, or the system's menu bar with its application menu
/// on a Mac; whether a save goes through a panel that asks; and which menus there are.
export interface MenuHost<T extends MenuTable = ShellMenus> {
  menus: 'page' | 'system'
  appMenu: boolean
  savePanel: boolean
  bar: MenuBarTable<T>
}

export function menuHost(menus: 'page' | 'system', platform: Platform): MenuHost
export function menuHost<T extends MenuTable>(menus: 'page' | 'system', platform: Platform, bar: MenuBarTable<T>): MenuHost<T>
export function menuHost(menus: 'page' | 'system', platform: Platform, bar: MenuBarTable<MenuTable> = SHELL_MENUS): MenuHost<MenuTable> {
  return { menus, appMenu: menus === 'system' && platform === 'mac', savePanel: menus === 'system', bar }
}

export type MenuItem =
  | { kind: 'command'; id: string; label: string; shown: string | null; accelerator: string | null; enabled: boolean; checked: boolean | null; system: SystemItem | null; reason?: string | null }
  | { kind: 'submenu'; label: string; items: MenuItem[] }
  | { kind: 'separator' }

export interface MenuModel<Id extends string = MenuId> {
  generation: number
  menus: { id: Id; label: string; items: MenuItem[] }[]
}

/// The menu and section a placement lands in on this host.
export function placedAt<S, T extends MenuTable = ShellMenus>(placement: Placed<S, T>, host: MenuHost<T>): { menu: MenuId<T>; section: string } {
  if (placement.menu === 'app' && !host.appMenu) return FOLD[placement.section as keyof typeof FOLD] as { menu: MenuId<T>; section: string }
  return { menu: placement.menu, section: placement.section }
}

/// The label as the host shows it: the placement's own or the title, with an ellipsis when the
/// item asks first.
export function itemLabel<S, T extends MenuTable = ShellMenus>(placement: Placed<S, T>, title: string, host: MenuHost<T>): string {
  const label = placement.label ?? title
  return placement.asks === true || (placement.asks === 'native' && host.savePanel) ? `${label}…` : label
}

const enabledNow = <S>(command: { when?: (shell: S) => boolean }, shell: S): boolean => !command.when || command.when(shell)

/// The menus a host shows, in order.
export function menusOf<T extends MenuTable = ShellMenus>(host: MenuHost<T>): MenuId<T>[] {
  return (host.bar.order as readonly MenuId<T>[]).filter(id => (id === 'app' ? host.appMenu : id === 'window' ? host.menus === 'system' : true))
}

/// A menu's name on the bar.
export function menuLabel<T extends MenuTable = ShellMenus>(host: MenuHost<T>, id: MenuId<T>): string {
  return host.bar.menus[id]!.label
}

/// The one tree every consumer draws. Pure.
export function menuModel<S, T extends MenuTable = ShellMenus>(registry: Registry<S, T>, shell: S, platform: Platform, host: MenuHost<T>, generation = 0): MenuModel<MenuId<T>> {
  const placed = registry.all().flatMap(c => (c.menu.kind === 'item' && (!c.menu.native || host.menus === 'system') && (!c.menu.present || c.menu.present(shell)) ? [{ command: c, placement: c.menu, where: placedAt(c.menu, host) }] : []))
  return {
    generation,
    menus: menusOf(host).map(id => {
      const items: MenuItem[] = []
      for (const section of host.bar.menus[id]!.sections) {
        const here = placed.filter(p => p.where.menu === id && p.where.section === section)
        if (here.length === 0) continue
        if (items.length) items.push({ kind: 'separator' })
        const submenus = new Map<string, MenuItem[]>()
        for (const { command, placement } of here) {
          const shown = shownChord(registry, shell, command.id, platform)
          const chord = placement.accelerator ? registry.chord(command.id, platform) : null
          const item: MenuItem = {
            kind: 'command',
            id: command.id,
            label: itemLabel(placement, command.title, host),
            shown: shown && displayChord(shown, platform),
            accelerator: chord,
            enabled: enabledNow(command, shell),
            checked: placement.checked ? placement.checked(shell) : null,
            system: placement.system ?? null,
          }
          const reason = item.kind === 'command' && !item.enabled && command.reason ? command.reason(shell) : null
          if (reason && item.kind === 'command') item.reason = reason
          if (!placement.submenu) {
            items.push(item)
            continue
          }
          const into = submenus.get(placement.submenu)
          if (into) into.push(item)
          else {
            const fresh = [item]
            submenus.set(placement.submenu, fresh)
            items.push({ kind: 'submenu', label: placement.submenu, items: fresh })
          }
        }
      }
      return { id, label: menuLabel(host, id), items }
    }),
  }
}

/// Enabled and checked per placed id, for the native host's revalidation. Pure.
export function menuStates<S, T extends MenuTable = ShellMenus>(registry: Registry<S, T>, shell: S): { id: string; enabled: boolean; checked: boolean | null }[] {
  return registry.all().flatMap(c => (c.menu.kind === 'item' && (!c.menu.present || c.menu.present(shell)) ? [{ id: c.id, enabled: enabledNow(c, shell), checked: c.menu.checked ? c.menu.checked(shell) : null }] : []))
}

/// The chord as every surface shows it: on the item the registry would dispatch it to now, or,
/// when none would, on the first placed item bound to it; null on every other. The editor's own
/// chords show where they are bound.
export function shownChord<S, T extends MenuTable = ShellMenus>(registry: Registry<S, T>, shell: S, id: string, platform: Platform): string | null {
  const chord = registry.chord(id, platform)
  if (!chord) return null
  const bound = registry.bound(chord, platform)
  if (!bound.some(c => c.id === id)) return chord
  const owner = bound.find(c => enabledNow(c, shell)) ?? bound.find(c => c.menu.kind === 'item') ?? bound[0]
  return owner?.id === id ? chord : null
}

/// The name of the menu a command sits in on this host, for the palette's trailing hint.
export function menuLabelOf<S, T extends MenuTable = ShellMenus>(placement: Placement<S, T>, host: MenuHost<T>): string | null {
  return placement.kind === 'item' ? menuLabel(host, placedAt(placement, host).menu) : null
}

/// The prefix of an Open Recent item's id; the rest is the project's key on this device, or
/// `held:` and the server project's id for a copy taken from the organisation.
export const RECENT = 'project.openRecent:'

/// Open Recent (docs/design/native-app.md, decision 18, and docs/design/menu-bar.md, note 2):
/// the projects on this device by when they were last opened, at most `limit` of them, then the
/// copies taken from the organisation's server by name. A project this device cannot read is
/// listed and disabled. The page builds it, so the app knows no listing rule. Pure.
export function recentMenu(projects: readonly HomeEntry[], held: Readonly<Record<string, { name: string }>>, limit = 10): MenuItem {
  const opened = projects.filter(p => !p.readable || p.lastOpened !== null)
  const at = (p: HomeEntry) => (p.readable && p.lastOpened ? p.lastOpened.at : '')
  const own: MenuItem[] = [...opened].sort((a, b) => at(b).localeCompare(at(a))).slice(0, limit).map(p => ({ kind: 'command', id: `${RECENT}${p.key}`, label: p.readable ? p.name : p.key, shown: null, accelerator: null, enabled: p.readable, checked: null, system: null }))
  const taken: MenuItem[] = Object.entries(held).sort(([, a], [, b]) => a.name.localeCompare(b.name)).map(([id, h]) => ({ kind: 'command', id: `${RECENT}held:${id}`, label: h.name, shown: null, accelerator: null, enabled: true, checked: null, system: null }))
  return { kind: 'submenu', label: 'Open Recent', items: own.length && taken.length ? [...own, { kind: 'separator' }, ...taken] : [...own, ...taken] }
}

/// The model with Open Recent in File, after Open a bundle.
export function withRecent(model: MenuModel, recent: MenuItem): MenuModel {
  return {
    ...model,
    menus: model.menus.map(m => {
      if (m.id !== 'file') return m
      const at = m.items.findIndex(i => i.kind === 'command' && i.id === 'project.openBundle')
      return at < 0 ? m : { ...m, items: [...m.items.slice(0, at + 1), recent, ...m.items.slice(at + 1)] }
    }),
  }
}

/// Every command item's id, enabled and checked, through the submenus, for the native host's
/// revalidation of the tree it installed.
export function itemStates(model: MenuModel): { id: string; enabled: boolean; checked: boolean | null }[] {
  const walk = (items: MenuItem[]): { id: string; enabled: boolean; checked: boolean | null }[] => items.flatMap(i => (i.kind === 'command' ? [{ id: i.id, enabled: i.enabled, checked: i.checked }] : i.kind === 'submenu' ? walk(i.items) : []))
  return model.menus.flatMap(m => walk(m.items))
}

/// The tree as the native host builds it, without what revalidation changes: two models with
/// the same shape need no reinstall.
export function menuShape(model: MenuModel): string {
  return JSON.stringify(model.menus, (key, value) => (key === 'enabled' || key === 'checked' ? undefined : value))
}

